import type { FamilyContact, HealthBackground, Mother } from '../types';
import { mothers } from './mockData';
import { addAudit, type Actor } from './auditService';
import { respond } from './respond';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { isReal, loadClinic } from './store';
import { scope } from './scope';

// "About" on the patient card: contact, family and background health. Any clinic role can edit
// (reception collects these at the first visit). None of it is ever sent to calls or WhatsApp.
// The audit log records THAT something changed, never the values (no phone numbers or allergies in logs).
const CLINIC_ROLES = ['facility_admin', 'doctor', 'staff'];
const guard = (actor?: Actor) => { if (!actor || !CLINIC_ROLES.includes(actor.role)) throw new Error('Not allowed'); };

export const ALLERGY_OPTIONS = ['penicillin', 'sulfa', 'nsaid', 'aspirin', 'iron_injection', 'latex', 'iodine', 'egg', 'other'] as const;
export const CONDITION_OPTIONS = ['diabetes', 'high_bp', 'thyroid', 'anaemia', 'asthma', 'epilepsy', 'heart', 'kidney', 'tb', 'other'] as const;
export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'unknown'] as const;
export const RELATIONS: FamilyContact['relation'][] = ['husband', 'mother', 'mother_in_law', 'father', 'sister', 'other'];

/** 10-digit Indian mobile (starts 6–9), with or without +91. Demo numbers (+91 55500 …) are allowed only in the demo. */
export function normalisePhone(raw: string): string | null {
  let d = raw.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  const ok = /^[6-9]\d{9}$/.test(d) || (!isReal() && /^55500\d{5}$/.test(d));
  return ok ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : null;
}

async function patch(motherId: string, fields: Partial<Mother>, actor: Actor | undefined, action: string): Promise<Mother> {
  guard(actor);
  const clean = JSON.parse(JSON.stringify(fields)) as Partial<Mother>;
  if (isReal()) {
    const fid = scope().facilityId;
    await updateDoc(doc(db!, 'facilities', fid, 'mothers', motherId), clean);
    addAudit(actor, action, `Patient ${motherId}`, fid);
    return (await loadClinic(fid)).mothers.find((x) => x.id === motherId)!;
  }
  const m = mothers.find((x) => x.id === motherId)!;
  Object.assign(m, clean);
  addAudit(actor, action, `Patient ${motherId}`, m.facilityId);
  return respond(m);
}

export async function updateContact(motherId: string, v: { phone: string; address: string }, actor?: Actor): Promise<Mother> {
  const phone = normalisePhone(v.phone);
  if (!phone) throw new Error('BAD_PHONE');
  return patch(motherId, { phone, address: v.address.trim() }, actor, 'patient.contact_updated');
}

export async function updateFamily(motherId: string, family: FamilyContact[], actor?: Actor): Promise<Mother> {
  const rows = family.filter((f) => f.name.trim() || f.phone.trim()).slice(0, 3).map((f) => {
    const phone = f.phone.trim() ? normalisePhone(f.phone) : '';
    if (phone === null) throw new Error('BAD_PHONE');
    return { name: f.name.trim(), relation: f.relation, phone };
  });
  return patch(motherId, { family: rows }, actor, 'patient.family_updated');
}

export async function updateHealth(motherId: string, h: HealthBackground, actor?: Actor): Promise<Mother> {
  const status = h.allergyStatus ?? 'unknown';
  const health: HealthBackground = {
    bloodGroup: h.bloodGroup ?? 'unknown',
    allergyStatus: status,
    allergies: status === 'has' ? h.allergies ?? [] : [],
    allergyNotes: status === 'has' ? h.allergyNotes?.trim() || undefined : undefined,
    conditions: h.conditions ?? [],
    conditionNotes: h.conditionNotes?.trim() || undefined,
    otherMedicines: h.otherMedicines?.trim() || undefined,
    previousPregnancies: h.previousPregnancies !== undefined && h.previousPregnancies >= 0 ? Math.min(Math.round(h.previousPregnancies), 20) : undefined,
    previousCSection: h.previousCSection,
    updatedAt: new Date().toISOString(),
    updatedBy: actor?.name,
  };
  if (status === 'has' && !health.allergies?.length && !health.allergyNotes) throw new Error('ALLERGY_DETAIL');
  return patch(motherId, { health }, actor, 'patient.health_updated');
}

/** Short allergy text for warnings, e.g. "Penicillin, Iron injection". Empty if none recorded. */
export const allergyKeys = (m: Mother) => (m.health?.allergyStatus === 'has' ? m.health.allergies ?? [] : []);
